import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { http, formatError } from '@/lib/api';
import { toast } from 'sonner';
import { BarChart3, Phone, Users2, CheckCircle2, PercentCircle, AlertTriangle } from 'lucide-react';

const DISP_LABELS = {
  no_answer: 'No answer',
  left_voicemail: 'Voicemail',
  conversation: 'Conversation',
  wrong_number: 'Wrong number',
  appointment_booked: 'Appointment booked',
  follow_up_needed: 'Follow-up needed',
  not_interested: 'Not interested',
  do_not_call: 'Do not call',
};

function Metric({ icon: Icon, label, value, unit }) {
  return (
    <div className="hq-card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="hq-eyebrow">{label}</span>
        <Icon size={16} style={{ color: 'var(--plum)' }} strokeWidth={1.4} />
      </div>
      <div className="font-serif text-3xl" style={{ color: 'var(--sage)' }}>
        {value ?? '—'}<span className="text-lg ml-1" style={{ color: 'var(--muted)' }}>{unit || ''}</span>
      </div>
    </div>
  );
}

export default function CallAnalytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    http.get('/call/analytics').then(r => setData(r.data)).catch(e => toast.error(formatError(e)));
  }, []);

  const total = data?.total_dials || 0;
  const byDisp = data?.by_disposition || {};
  const maxCount = Math.max(1, ...Object.values(byDisp));

  return (
    <div className="space-y-6">
      <header className="hq-fade-in">
        <div className="flex items-center gap-3">
          <Link to="/call" className="hq-eyebrow" style={{ color: 'var(--gold-soft)' }}>← Call Command</Link>
        </div>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="analytics-title">Analytics</h1>
        <div className="hq-divider-gold w-fit"></div>
        <p className="mt-4 max-w-2xl text-sm" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
          Meaningful conversations over raw dial volume. Homi tracks quality signals — appointments,
          answered rate, wrong numbers, DNC requests — so you calibrate the campaign, not just push harder.
        </p>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-metrics">
        <Metric icon={Phone} label="Total Dials" value={total} />
        <Metric icon={PercentCircle} label="Answer Rate" value={data?.answer_rate} unit="%" />
        <Metric icon={Users2} label="Conversation Rate" value={data?.conversation_rate} unit="%" />
        <Metric icon={CheckCircle2} label="Appointments" value={data?.appointments} />
        <Metric icon={Users2} label="Meaningful Convos" value={data?.meaningful_conversations} />
        <Metric icon={BarChart3} label="Calls / Appt" value={data?.calls_per_appointment ?? '—'} />
        <Metric icon={AlertTriangle} label="Wrong Number %" value={data?.wrong_number_rate} unit="%" />
        <Metric icon={AlertTriangle} label="DNC Request %" value={data?.dnc_request_rate} unit="%" />
      </section>

      <div className="hq-card p-6">
        <div className="hq-plum-eyebrow mb-3">Outcomes Breakdown</div>
        {total === 0 && (
          <div className="text-sm py-8 text-center" style={{ color: 'var(--muted)' }}>
            No calls logged yet. Place a call from Call Command to build your first data point.
          </div>
        )}
        {total > 0 && (
          <div className="space-y-3" data-testid="disposition-breakdown">
            {Object.entries(byDisp).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3 text-sm">
                <div className="w-44 uppercase tracking-widest" style={{ color: 'var(--muted)', fontSize: '0.72rem' }}>
                  {DISP_LABELS[k] || k}
                </div>
                <div className="flex-1 h-2 rounded-full" style={{ background: 'var(--line)' }}>
                  <div className="h-full rounded-full" style={{ width: `${(v/maxCount)*100}%`, background: k === 'do_not_call' ? 'var(--danger)' : k === 'appointment_booked' ? 'var(--success)' : 'var(--plum)' }} />
                </div>
                <div style={{ color: 'var(--ink)' }} className="w-8 text-right">{v}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="hq-card p-6 text-sm" style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
        <div className="hq-plum-eyebrow mb-2">Why We Don't Rank On Volume</div>
        Homi Call Command rewards meaningful conversations, kept appointments, follow-through, and
        respectful compliance — never raw dial counts. Volume-first cultures produce burnout, sloppy
        opt-out handling, and complaints. Quality-first cultures produce clients.
      </div>
    </div>
  );
}
